import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TouchableHighlight, View, Linking, Platform } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import Text from 'design-system/Text';
import { rem } from 'design-system/values';
import StatusBar from 'design-system/StatusBar';
import { IconDocument } from 'design-system/icons';
import RadioButton from 'design-system/RadioButton';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { Button, CardView, NavigationHeader, Spacer } from 'design-system';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import TouchableBounce from 'utils/packages/TouchableBounce';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { savePotsOnboardingState } from 'features/pots/actions';
import useCreatePot from 'features/pots/hooks/useCreatePot';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import { selectIsPotsAccountActive } from 'features/pots/reducers/selectors';
import SectionHeader from 'design-system/SectionHeader';
import { useCommonInvestStyles } from './styles';
import * as constants from '../constants';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { useCreateInvestAccount } from '../hooks/useCreateInvestAccount';
import { useConnectedAccount, useOnboardingState } from '../hooks/selectors';
import { selectHideFundingAccountAndAutoinvestFromInvestOnboarding } from '../reducers/selectors';
const externalDocuments = [
    { title: 'S&S ISA key features', url: constants.SECCL_ISA_KEY_FEATURES },
    { title: 'ISA declaration', url: constants.SECCL_ISA_DECLARATION },
];
const JISAExternalDocuments = [
    { title: 'Junior ISA key features', url: constants.SECCL_JISA_KEY_FEATURES },
    { title: 'Junior ISA declaration', url: constants.SECCL_JISA_DECLARATION },
];
const cashISAExternalDocuments = [
    { title: 'Cash ISA key features', url: constants.SECCL_CASH_ISA_KEY_FEATURES },
    { title: 'ISA declaration', url: constants.SECCL_CASH_ISA_DECLARATION },
];
const SecclISADeclarationScreen = ({ navigation, route: { params }, }) => {
    const dispatch = useAppDispatch();
    const scrollY = useSharedValue(0);
    const commonStyles = useCommonInvestStyles();
    const styles = useStyles(styleSet);
    const colors = useColors();
    const accountType = useAppSelector((state) => state.invest.onboarding?.accountType);
    const onboardingState = useOnboardingState();
    const connectedAccount = useConnectedAccount();
    const hideFundingAccountAndAutoinvestFlow = useAppSelector(selectHideFundingAccountAndAutoinvestFromInvestOnboarding);
    const { submit } = useCreateInvestAccount();
    const createPotParams = params && 'createPotParams' in params ? params.createPotParams : undefined;
    const isJISA = accountType === 'JISA' && !createPotParams;
    const [isChecked, setIsChecked] = useState(false);
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextPotsScreen = useNavigateToNextScreenForPotsOnboarding();
    const potsOnboardingState = useAppSelector((state) => state.pots.onboarding);
    const isPotsAccountActive = useAppSelector(selectIsPotsAccountActive);
    const { createNewPot, creatingPotRequesting } = useCreatePot();
    const saveEnabledRef = useRef(true);
    const onPressCheck = useCallback(() => {
        setIsChecked((prev) => !prev);
    }, []);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const handleContinue = useCallback(async () => {
        if (createPotParams) {
            if (potsOnboardingState && !isPotsAccountActive) {
                dispatch(savePotsOnboardingState({ createPotsParams: createPotParams }));
                navigateToNextPotsScreen();
                return;
            }
            if (!saveEnabledRef.current)
                return;
            saveEnabledRef.current = false;
            await createNewPot(createPotParams);
            saveEnabledRef.current = true;
            return;
        }
        if (hideFundingAccountAndAutoinvestFlow || (onboardingState?.hasJISAOrTrustFund && connectedAccount)) {
            submit();
            return;
        }
        dispatch({ type: 'SAVE_ONBOARDING_STATE', didSignDeclaration: true, params: {} });
        navigateToNextScreen();
    }, [
        connectedAccount,
        createNewPot,
        createPotParams,
        dispatch,
        hideFundingAccountAndAutoinvestFlow,
        isPotsAccountActive,
        navigateToNextPotsScreen,
        navigateToNextScreen,
        onboardingState?.hasJISAOrTrustFund,
        potsOnboardingState,
        submit,
    ]);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y > 0 ? contentOffset.y : 0;
    });
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const openExternalDocument = useCallback((url) => () => {
        if (Platform.OS === 'android') {
            navigation.navigate('Pdf', { url });
        }
        else {
            Linking.openURL(url);
        }
    }, []);
    const documents = useMemo(() => {
        if (createPotParams) {
            return cashISAExternalDocuments;
        }
        if (isJISA) {
            return JISAExternalDocuments;
        }
        return externalDocuments;
    }, [createPotParams, isJISA]);
    if (!accountType && !createPotParams) {
        return null;
    }
    return (<View style={commonStyles.containerNoPadding}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} outputColors={headerColors} rightContent={<HelpScreenButton />}/>
      <Animated.ScrollView scrollEnabled scrollEventThrottle={16} onScroll={scrollHandler} keyboardShouldPersistTaps="handled" contentContainerStyle={commonStyles.contentContainer}>
        <ScreenInfoHeader title={isJISA ? 'Junior ISA declaration' : 'ISA declaration'} subTitle={isJISA
            ? 'In addition to our T&Cs, please read and agree to the Junior ISA declaration'
            : 'In addition to our T&Cs, please read and agree to the ISA declaration'}/>
        <CardView withPadding noMarginTop>
          <Text TextThin-14>
            {isJISA
            ? `This is to confirm that any regular or single subscriptions paid for not exceed Junior ISA subscription for the children below. Please note if you have subscribed to another stocks and shares Junior ISA in this tax year but intend to transfer all the current year subscriptions from that Junior ISA to this Junior ISA, you can agree to this statement.`
            : `This is to confirm that you are eligible to subscribe to your ISA. You have not subscribed/made payments, and will not subscribe/make payments, more than the overall subscription/payment limit in total to any cash ISAs, Stocks & Shares ISAs, innovative finance ISAs, and a Lifetime ISA in the same tax year.`}
          </Text>
        </CardView>
        <TouchableBounce onPress={onPressCheck}>
          <View style={styles.itemContainer}>
            <RadioButton type="multiple" isChecked={isChecked}/>
            <Spacer.W16 />
            <View style={styles.flex}>
              <Text TextThin-14 flex>
                I agree with {isJISA ? 'Junior ISA' : 'ISA'} declaration
              </Text>
            </View>
          </View>
        </TouchableBounce>
        <SectionHeader largeVerticalMargins horizontalMargins>
          Other information
        </SectionHeader>
        <CardView noMarginTop>
          {documents.map(({ title, url }) => (<TouchableHighlight key={title} activeOpacity={1} underlayColor={colors.background.underlay} onPress={openExternalDocument(url)}>
              <View style={styles.documentBtn}>
                <View style={styles.iconContainer}>
                  <IconDocument colorKey="brand"/>
                </View>
                <Spacer w={16}/>
                <View style={styles.flex}>
                  <Text Text-16>{title}</Text>
                  <Text TextThin-14 Primary>
                    Review
                  </Text>
                </View>
              </View>
            </TouchableHighlight>))}
        </CardView>
      </Animated.ScrollView>
      <Button brand title="Confirm" testID="ContinueBtn" disabled={!isChecked} onPress={handleContinue} isFetching={creatingPotRequesting} style={commonStyles.button}/>
    </View>);
};
const styleSet = createStyleSheets((colors) => ({
    flex: {
        flex: 1,
    },
    itemContainer: {
        flexDirection: 'row',
        padding: rem(16),
        marginHorizontal: rem(16),
        alignItems: 'center',
        borderRadius: rem(16),
        backgroundColor: colors.cards.onDark,
    },
    documentBtn: {
        padding: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    iconContainer: {
        width: rem(44),
        height: rem(44),
        borderRadius: rem(12),
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: colors.background.brandLight,
    },
}));
export default SecclISADeclarationScreen;
